// admin-web HTTP client ??thin adapter over `service-client`'s
// `HttpServiceClient`.
//
// The legacy `createAdminHttp(opts)` factory kept the screens' preferred
// "no-arg" call style (the access token is pulled from `opts.getAccessToken`
// on every call) and the `findNewAuditId` / `latestAuditId` audit helpers.
// The actual transport is the unified `HttpServiceClient` from
// `@veluga/service-client`; we don't re-implement fetch / idempotency
// handling here.
//
// Idempotency-Key auto-injection for admin writes is covered by
// `HttpServiceClient` and the legacy test in
// `tests/admin-web/admin-web.test.ts` keeps passing against this adapter.

import {
  createServiceClient,
  type AllocationBatchInput,
  type AllocationBatchResult,
  type ServiceClient,
  ServiceClientError,
  type ModelRouteAdminView,
  type PartnerCustomModelDeployment,
  type PartnerCustomModelSummary,
  type PartnerCustomModelTestRequest,
  type PartnerCustomModelTestResult,
  type PartnerCustomModelWrite,
  type UpdatePartnerCustomModelRequest,
  type UpdatePartnerCustomModelResult,
  type CapabilitySourceRegisterInput,
} from '../api.js';
import type {
  AdminAuditEvent,
  AdminGroupView,
  AdminOverview,
  AdminUserView,
  AutoRoutingPreviewRequest,
  AutoRoutingPreviewResult,
  AutoRoutingDecisionsView,
  AutoRoutingProfileView,
  AutoRoutingProfileWrite,
  AllocationOverview,
  Announcement,
  AnnouncementListQuery,
  AnnouncementScope,
  AuditListQuery,
  CreateAnnouncementRequest,
  PatchAnnouncementRequest,
  CreditAllocation,
  CreditLimitView,
  EffectiveCreditLimitsRequest,
  EffectiveCreditLimitsView,
  UpsertCreditLimitRequest,
  CreateQuotaProfileRequest,
  MembershipQuotaAssignment,
  QuotaProfileAssignmentPreview,
  QuotaProfileLimitInput,
  QuotaProfileListView,
  QuotaProfileView,
  UpdateQuotaProfileRequest,
  UserQuotaAssignmentQuery,
  UserQuotaAssignmentView,
  DesktopBootstrap,
  DiagnosticBundleListQuery,
  DiagnosticBundleView,
  DesktopModelView,
  DesktopPlatform,
  DownloadChannel,
  DownloadManifest,
  Entitlement,
  Group,
  InvitedUser,
  ListQuery,
  MemberQuotaDefault,
  ModelRoute,
  OrganizationSsoConnectionAdmin,
  OrganizationSsoConnectionPatch,
  OrganizationSsoConnectionTestInput,
  OrganizationSsoConnectionTestResult,
  OrganizationSsoConnectionWrite,
  OrganizationSsoMode,
  Paginated,
  Partner,
  PartnerAdminView,
  PartnerOverview,
  PlatformAdminView,
  PartnerWebSearchUsageSummary,
  PlatformProviderKeyProvider,
  PlatformProviderModelDefinition,
  PlatformProviderModelDeployment,
  PlatformProviderModelListQuery,
  PlatformProviderModelRateInput,
  PlatformProviderModelTestResult,
  CreatePlatformProviderModelRequest,
  DeployPlatformProviderModelResult,
  BulkDeployPlatformProviderModelsRequest,
  BulkDeployPlatformProviderModelsResult,
  CapabilitySourceAssignment,
  PublishPlatformProviderModelRatesResult,
  PlatformWebSearchKeySummary,
  ProviderKeyRegistrationResult,
  ProviderKeyRemovalResult,
  ProviderKeySummary,
  ProviderKeyTestResult,
  PolicyContextSnapshot,
  QuotaPolicy,
  ServiceRole,
  ServiceUser,
  SocialLoginMode,
  SocialProviderConfig,
  SocialProviderKind,
  Tenant,
  TenantPartnerTransferResult,
  TenantOrganizationSsoConfig,
  TenantAssignment,
  TenantLoginDefaultModelView,
  TenantMcpConnectors,
  TenantSelfSignupResult,
  TenantSocialConfig,
  TenantSocialProvider,
  TenantWebSearchBackend,
  TenantWebSearchBackendSummary,
  TenantMediaRoute,
  TenantMediaRouteSummary,
  TestLiteLLMConnectionInput,
  TestProviderKeyInput,
  TenantModelAccess,
  DeploymentProfile,
  DefaultSignupTenantSetting,
  CreditRateTableView,
  CreditRateSettingsView,
  PutCreditRateSettingsRequest,
  PutCreditRateSettingsResult,
  UsageChainVerification,
  UsageEventView,
  UsageQuery,
  CreditSettlementQuery,
  CreditSettlementReport,
  UsageSeriesPoint,
  UsageSeriesQuery,
  UsageSummaryView,
  WebSearchNativeProvider,
  UserListQuery,
  CapabilitySourceRecord,
  ExtensionReleaseView,
} from '../../../shared-types/src/index.js';
import type { MergePolicyInput } from '../../../policy-service/src/merge.js';
import type { PlatformBillingAdminMethods } from './billing.js';
import type { SessionProtocol, SessionTokenPair, SessionCapabilities, RotatingTokenPair, SessionRevokeInput } from '../../../shared-types/src/session.js';
import { validateGatewayBaseUrl } from '../../../shared-types/src/gateway-url.js';

/**
 * Options bag for `createAdminHttp`. Kept structurally identical to the
 * pre-service-client implementation so the Login screen + the legacy
 * Idempotency-Key test do not need to change shape.
 */
export interface AdminHttpOptions {
  baseUrl: string;
  getAccessToken: () => string | undefined;
  fetchImpl?: typeof fetch;
  randomUUID?: () => string;
}

export { ServiceClientError as AdminApiError };

/** Credit-allocation write fields (grantee supplied by the method). */
export interface AllocationFields {
  creditMillis?: number;
  inputTokens?: number;
  outputTokens?: number;
  unlimited?: boolean;
  note?: string;
}

export type AllocationBatchFields = Omit<
  AllocationBatchInput,
  'idempotencyKey'
>;

export type MemberQuotaDefaultFields = Pick<
  MemberQuotaDefault,
  'nodeKind' | 'nodeId' | 'perMemberCreditMillis' | 'mode'
> & {
  /** Omitted = 'lifetime', the pre-cycle contract. */
  cycle?: MemberQuotaDefault['cycle'];
};

/**
 * The admin-web client surface. Every method is `no-arg` for the
 * bearer token (it is pulled from `opts.getAccessToken` at call time).
 * Admin write methods take an `idempotencyKey` as their last argument; if
 * the caller omits it we auto-generate one.
 */
export interface AdminHttpClient extends PlatformBillingAdminMethods {
  // auth
  loginWithPassword(
    email: string,
    password: string,
    tenantId?: string,
    sessionProtocol?: SessionProtocol
  ): Promise<SessionTokenPair>;
  platformLoginWithPassword(
    email: string,
    password: string,
    sessionProtocol?: SessionProtocol
  ): Promise<SessionTokenPair>;
  partnerLoginWithPassword(
    email: string,
    password: string,
    sessionProtocol?: SessionProtocol
  ): Promise<SessionTokenPair & {
    partnerId: string;
  }>;
  sessionCapabilities(): Promise<SessionCapabilities>;
  refreshSession(refreshToken: string): Promise<RotatingTokenPair>;
  revokeSession(input: SessionRevokeInput): Promise<{ ok: true }>;
  /** Trade the stored refresh token for a fresh access token. Admin-web logs in
   *  through the shared password route, which mints a short-lived (15-min)
   *  access token ??the app shell uses this on a 401 to keep an open console
   *  alive instead of forcing a re-login. */
  refresh(
    refreshToken: string
  ): Promise<{ accessToken: string; expiresAt: string }>;
  // platform: tenants
  /** Without `query` ??legacy full array; with `query` ??`Paginated` envelope
   *  (same dual-mode contract on `listUsers`/`listAudit`). */
  getTenants(): Promise<Tenant[]>;
  getTenants(query: ListQuery): Promise<Paginated<Tenant>>;
  createTenant(
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
      partnerId?: string;
    },
    idempotencyKey?: string
  ): Promise<Tenant>;
  deleteTenant(
    tenantId: string,
    confirmName: string,
    idempotencyKey?: string
  ): Promise<void>;
  transferTenantPartner(
    tenantId: string,
    input: {
      partnerId: string | null;
      confirmName?: string;
      dryRun?: boolean;
    },
    idempotencyKey?: string
  ): Promise<TenantPartnerTransferResult>;
  actAsTenant(tenantId: string): Promise<{
    accessToken: string;
    expiresAt: string;
    tenantId: string;
    roles: ServiceRole[];
  }>;
  getRealtimeVoice(tenantId: string): Promise<import('../../../shared-types/src/realtime.js').RealtimeVoiceAdminView>;
  putRealtimeVoice(tenantId: string, input: import('../../../shared-types/src/realtime.js').RealtimeVoiceAdminWrite, idempotencyKey?: string): Promise<import('../../../shared-types/src/realtime.js').RealtimeVoiceAdminView>;
  getVoiceSettings(): Promise<import('../../../shared-types/src/realtime.js').VoiceFeatureSettings>;
  putVoiceSettings(input: import('../../../shared-types/src/realtime.js').VoiceFeatureSettingsWrite, idempotencyKey?: string): Promise<import('../../../shared-types/src/realtime.js').VoiceFeatureSettings>;
  getTenantVoice(tenantId: string): Promise<import('../../../shared-types/src/realtime.js').TenantVoiceAdminView>;
  putTenantVoice(tenantId: string, input: import('../../../shared-types/src/realtime.js').TenantVoicePolicyWrite, idempotencyKey?: string): Promise<import('../../../shared-types/src/realtime.js').TenantVoiceAdminView>;
  postTenantVoiceModel(tenantId: string, input: import('../../../shared-types/src/realtime.js').VoiceModelAdminWrite, idempotencyKey?: string): Promise<import('../../../shared-types/src/realtime.js').TenantVoiceAdminView>;
  getRecommendedModelProfile(tenantId: string): Promise<import('../../../shared-types/src/recommended-models.js').RecommendedModelProfileView>;
  putRecommendedModelProfile(tenantId: string, profile: import('../../../shared-types/src/recommended-models.js').RecommendedModelProfileWrite, expectedRevision: number, idempotencyKey?: string): Promise<import('../../../shared-types/src/recommended-models.js').RecommendedModelProfileView>;
  getAutoRoutingProfile(tenantId: string): Promise<AutoRoutingProfileView>;
  putAutoRoutingProfile(
    tenantId: string,
    profile: AutoRoutingProfileWrite,
    idempotencyKey?: string
  ): Promise<AutoRoutingProfileView>;
  previewAutoRouting(
    tenantId: string,
    preview: AutoRoutingPreviewRequest
  ): Promise<AutoRoutingPreviewResult>;
  listAutoRoutingDecisions(tenantId: string, limit?: number): Promise<AutoRoutingDecisionsView>;
  // platform: partners
  listPartners(): Promise<Partner[]>;
  platformDiagnosticBundles(
    query?: DiagnosticBundleListQuery
  ): Promise<Paginated<DiagnosticBundleView>>;
  platformDiagnosticBundleDownload(
    id: string
  ): Promise<{ url: string; expiresAt: string }>;
  createPartner(
    input: { name: string; slug?: string; tenantCap?: number },
    idempotencyKey?: string
  ): Promise<Partner>;
  getPartner(partnerId: string): Promise<Partner>;
  updatePartner(
    partnerId: string,
    patch: {
      tenantCap?: number | null;
      status?: 'active' | 'suspended';
      allowCustomModels?: boolean;
    },
    idempotencyKey?: string
  ): Promise<Partner>;
  deletePartner(
    partnerId: string,
    confirmName: string,
    idempotencyKey?: string
  ): Promise<void>;
  setTenantCustomModels(
    tenantId: string,
    allowCustomModels: boolean,
    idempotencyKey?: string
  ): Promise<Tenant>;
  // Returns the tenant *plus* an optional `fundedWarning` the server computes
  // when self-signup is allowed on a tenant with no credit allocation. Typing
  // this as `Tenant` silently dropped that warning on the floor.
  setTenantSelfSignup(
    tenantId: string,
    patch: { allowed?: boolean; enabled?: boolean },
    idempotencyKey?: string
  ): Promise<TenantSelfSignupResult>;
  getDefaultSignupTenant(): Promise<DefaultSignupTenantSetting>;
  setDefaultSignupTenant(
    tenantId: string | null,
    idempotencyKey?: string
  ): Promise<DefaultSignupTenantSetting>;
  setTenantMcpConnectors(
    tenantId: string,
    activeMcpConnectors: string[],
    idempotencyKey?: string
  ): Promise<Tenant>;
  setTenantDeepAgentApprovalTools(
    tenantId: string,
    deepAgentApprovalTools: string[],
    idempotencyKey?: string
  ): Promise<Tenant>;
  getTenantMcpConnectors(tenantId: string): Promise<TenantMcpConnectors>;
  getOwnMcpConnectors(): Promise<TenantMcpConnectors>;
  // platform: social login (SSO)
  getSocialConfig(tenantId: string): Promise<TenantSocialConfig>;
  updateSocialMode(
    tenantId: string,
    socialLoginMode: SocialLoginMode,
    idempotencyKey?: string
  ): Promise<TenantSocialConfig>;
  createSocialProvider(
    tenantId: string,
    input: {
      provider: SocialProviderKind;
      displayName: string;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
    },
    idempotencyKey?: string
  ): Promise<TenantSocialProvider>;
  updateSocialProvider(
    tenantId: string,
    providerId: string,
    patch: {
      displayName?: string;
      enabled?: boolean;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
    },
    idempotencyKey?: string
  ): Promise<TenantSocialProvider>;
  deleteSocialProvider(
    tenantId: string,
    providerId: string,
    idempotencyKey?: string
  ): Promise<void>;
  getOrganizationSsoConfig(tenantId: string): Promise<TenantOrganizationSsoConfig>;
  updateOrganizationSsoMode(
    tenantId: string,
    mode: OrganizationSsoMode,
    confirmBreakGlass?: boolean,
    idempotencyKey?: string
  ): Promise<TenantOrganizationSsoConfig>;
  createOrganizationSsoConnection(
    tenantId: string,
    connection: OrganizationSsoConnectionWrite,
    idempotencyKey?: string
  ): Promise<OrganizationSsoConnectionAdmin>;
  updateOrganizationSsoConnection(
    tenantId: string,
    connectionId: string,
    patch: OrganizationSsoConnectionPatch,
    idempotencyKey?: string
  ): Promise<OrganizationSsoConnectionAdmin>;
  deleteOrganizationSsoConnection(
    tenantId: string,
    connectionId: string,
    idempotencyKey?: string
  ): Promise<void>;
  testOrganizationSsoConnection(
    tenantId: string,
    connectionId: string,
    test?: OrganizationSsoConnectionTestInput,
    idempotencyKey?: string
  ): Promise<OrganizationSsoConnectionTestResult>;
  setOrganizationSsoConnectionEnabled(
    tenantId: string,
    connectionId: string,
    enabled: boolean,
    idempotencyKey?: string
  ): Promise<OrganizationSsoConnectionAdmin>;
  getOrganizationSsoSamlMetadata(tenantId: string, connectionId: string): Promise<string>;
  listPartnerAdmins(partnerId: string): Promise<PartnerAdminView[]>;
  listPlatformAdmins(): Promise<PlatformAdminView[]>;
  grantPartnerAdmin(
    partnerId: string,
    input: { email: string; name: string; password: string },
    idempotencyKey?: string
  ): Promise<{ partnerId: string; userId: string }>;
  revokePartnerAdmin(
    partnerId: string,
    userId: string,
    idempotencyKey?: string
  ): Promise<{ ok: true }>;
  getPartnerModelScope(partnerId: string): Promise<string[]>;
  setPartnerModelScope(
    partnerId: string,
    modelKeys: string[],
    idempotencyKey?: string
  ): Promise<string[]>;
  listPartnerCustomModels(
    partnerId: string
  ): Promise<PartnerCustomModelSummary[]>;
  registerPartnerCustomModel(
    partnerId: string,
    definition: PartnerCustomModelWrite,
    idempotencyKey?: string
  ): Promise<PartnerCustomModelSummary>;
  /** Merge-patch under compare-and-swap on `revision`. Editing replaces the old
   *  delete-and-re-register workaround, which destroyed model assignments and
   *  user/group allowlists. */
  patchPartnerCustomModel(
    partnerId: string,
    modelKey: string,
    request: UpdatePartnerCustomModelRequest,
    idempotencyKey?: string
  ): Promise<UpdatePartnerCustomModelResult>;
  /** Diagnostics. No idempotency key: a retry must reach the network again. */
  testPartnerCustomModelProvider(
    partnerId: string,
    request: PartnerCustomModelTestRequest
  ): Promise<PartnerCustomModelTestResult>;
  testPartnerCustomModelLlm(
    partnerId: string,
    request: PartnerCustomModelTestRequest
  ): Promise<PartnerCustomModelTestResult>;
  deletePartnerCustomModel(
    partnerId: string,
    modelKey: string,
    idempotencyKey?: string
  ): Promise<void>;
  platformDeployCustomModel(
    tenantId: string,
    customModelKey: string,
    idempotencyKey?: string
  ): Promise<PartnerCustomModelDeployment>;
  platformRevokeCustomModel(
    tenantId: string,
    customModelKey: string,
    idempotencyKey?: string
  ): Promise<void>;
  getPartnerMcpConnectorScope(partnerId: string): Promise<string[]>;
  setPartnerMcpConnectorScope(
    partnerId: string,
    connectorIds: string[],
    idempotencyKey?: string
  ): Promise<string[]>;
  // capability sources (skill-agent-distribution PR10)
  listCapabilitySources(): Promise<CapabilitySourceRecord[]>;
  registerCapabilitySource(
    input: CapabilitySourceRegisterInput,
    idempotencyKey?: string
  ): Promise<CapabilitySourceRecord>;
  deleteCapabilitySource(
    id: string,
    idempotencyKey?: string
  ): Promise<{ success: boolean }>;
  setPartnerCapabilitySourceScope(
    partnerId: string,
    sourceIds: string[],
    idempotencyKey?: string
  ): Promise<string[]>;
  assignTenantCapabilitySource(
    tenantId: string,
    sourceId: string,
    idempotencyKey?: string
  ): Promise<CapabilitySourceAssignment>;
  revokeTenantCapabilitySource(
    tenantId: string,
    sourceId: string,
    idempotencyKey?: string
  ): Promise<void>;
  // partner console (scope==='partner' token)
  partnerListTenants(): Promise<Tenant[]>;
  partnerCreateTenant(
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
    },
    idempotencyKey?: string
  ): Promise<Tenant>;
  partnerActAsTenant(tenantId: string): Promise<{
    accessToken: string;
    expiresAt: string;
    tenantId: string;
    roles: ServiceRole[];
  }>;
  partnerSelf(): Promise<Partner>;
  partnerOverview(query?: Pick<UsageQuery, 'from' | 'to'>): Promise<PartnerOverview>;
  partnerListAdmins(): Promise<PartnerAdminView[]>;
  partnerGrantAdmin(input: { email: string; name: string; password?: string }, idempotencyKey?: string): Promise<{ partnerId: string; userId: string }>;
  partnerRevokeAdmin(userId: string, idempotencyKey?: string): Promise<void>;
  partnerSelfModelScope(): Promise<string[]>;
  partnerListCustomModels(): Promise<PartnerCustomModelSummary[]>;
  partnerGetTenantAssignment(tenantId: string): Promise<TenantAssignment>;
  partnerSetTenantAssignment(
    tenantId: string,
    allowedModelIds: string[],
    idempotencyKey?: string
  ): Promise<TenantAssignment>;
  partnerGetTenantMcpConnectors(tenantId: string): Promise<TenantMcpConnectors>;
  partnerSetTenantMcpConnectors(
    tenantId: string,
    activeMcpConnectors: string[],
    idempotencyKey?: string
  ): Promise<Tenant>;
  partnerSetTenantSelfSignup(
    tenantId: string,
    enabled: boolean,
    idempotencyKey?: string
  ): Promise<TenantSelfSignupResult>;
  partnerSetTenantDeepAgentApprovalTools(
    tenantId: string,
    deepAgentApprovalTools: string[],
    idempotencyKey?: string
  ): Promise<Tenant>;
  // credit allocation (Phase 2)
  setTenantAllocation(
    tenantId: string,
    input: AllocationFields,
    idempotencyKey?: string
  ): Promise<CreditAllocation>;
  getTenantAllocation(tenantId: string): Promise<AllocationOverview>;
  setPartnerAllocation(
    partnerId: string,
    input: AllocationFields,
    idempotencyKey?: string
  ): Promise<CreditAllocation>;
  getPartnerAllocation(partnerId: string): Promise<AllocationOverview>;
  getPartnerWebSearchUsage(
    partnerId: string,
    query?: Pick<UsageQuery, 'from' | 'to'>
  ): Promise<PartnerWebSearchUsageSummary>;
  partnerGetOwnAllocation(): Promise<AllocationOverview>;
  partnerGetOwnWebSearchUsage(
    query?: Pick<UsageQuery, 'from' | 'to'>
  ): Promise<PartnerWebSearchUsageSummary>;
  partnerSetTenantAllocation(
    tenantId: string,
    input: AllocationFields,
    idempotencyKey?: string
  ): Promise<CreditAllocation>;
  partnerGetTenantAllocation(tenantId: string): Promise<AllocationOverview>;
  tenantGetOwnAllocation(): Promise<AllocationOverview>;
  setSubjectAllocation(
    granteeKind: 'group' | 'user',
    granteeId: string,
    input: AllocationFields,
    idempotencyKey?: string
  ): Promise<CreditAllocation>;
  setSubjectAllocationsBatch(
    input: AllocationBatchFields,
    idempotencyKey?: string
  ): Promise<AllocationBatchResult>;
  getSubjectAllocation(
    granteeKind: 'group' | 'user',
    granteeId: string
  ): Promise<AllocationOverview>;
  setMemberQuotaDefault(
    input: MemberQuotaDefaultFields,
    idempotencyKey?: string
  ): Promise<MemberQuotaDefault>;
  getMemberQuotaDefault(
    nodeKind: MemberQuotaDefault['nodeKind'],
    nodeId: string
  ): Promise<MemberQuotaDefault | null>;
  // usage limits (narrow credit-limit API)
  listCreditLimits(): Promise<{
    items: CreditLimitView[];
    advancedPolicyCount: number;
  }>;
  upsertCreditLimit(
    input: UpsertCreditLimitRequest,
    idempotencyKey?: string
  ): Promise<CreditLimitView>;
  previewEffectiveCreditLimits(
    input: EffectiveCreditLimitsRequest
  ): Promise<EffectiveCreditLimitsView>;
  deleteCreditLimit(
    id: string,
    idempotencyKey?: string
  ): Promise<{ ok: true; deleted: boolean }>;
  // rank/standing quota profiles
  listQuotaProfiles(): Promise<QuotaProfileListView>;
  createQuotaProfile(
    input: CreateQuotaProfileRequest,
    idempotencyKey?: string
  ): Promise<QuotaProfileView>;
  updateQuotaProfile(
    input: UpdateQuotaProfileRequest & { id: string },
    idempotencyKey?: string
  ): Promise<QuotaProfileView>;
  deactivateQuotaProfile(id: string, idempotencyKey?: string): Promise<QuotaProfileView>;
  previewQuotaProfileImpact(input: {
    id: string;
    limits?: QuotaProfileLimitInput[];
    status?: 'active' | 'inactive';
  }): Promise<QuotaProfileAssignmentPreview>;
  listUserQuotaAssignments(
    query?: UserQuotaAssignmentQuery
  ): Promise<Paginated<UserQuotaAssignmentView>>;
  assignQuotaProfile(
    input: { userId: string; quotaProfileId: string },
    idempotencyKey?: string
  ): Promise<MembershipQuotaAssignment>;
  clearQuotaProfilePin(
    userId: string,
    idempotencyKey?: string
  ): Promise<MembershipQuotaAssignment | null>;
  // managed announcements. Unlike the rest of this interface these five take the
  // SURFACE as their first argument instead of appearing three times under
  // different names: the three consoles share one component, and a fix applied
  // to two of three named triples would be a silent drift. The transport keeps
  // its 1:1 named methods (`listPartnerAnnouncements`, …).
  listAnnouncements(
    scope: AnnouncementScope,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>>;
  createAnnouncement(
    scope: AnnouncementScope,
    input: CreateAnnouncementRequest,
    idempotencyKey?: string
  ): Promise<Announcement>;
  patchAnnouncement(
    scope: AnnouncementScope,
    input: PatchAnnouncementRequest & { id: string },
    idempotencyKey?: string
  ): Promise<Announcement>;
  publishAnnouncement(
    scope: AnnouncementScope,
    id: string,
    idempotencyKey?: string
  ): Promise<Announcement>;
  archiveAnnouncement(
    scope: AnnouncementScope,
    id: string,
    idempotencyKey?: string
  ): Promise<Announcement>;
  // self
  bootstrap(): Promise<DesktopBootstrap>;
  overview(): Promise<AdminOverview>;
  listExtensionReleases(extensionId?: string, platform?: 'win32' | 'darwin'): Promise<ExtensionReleaseView[]>;
  promoteExtensionRelease(extensionId: string, version: string, platform?: 'win32' | 'darwin', idempotencyKey?: string): Promise<void>;
  rollbackExtensionRelease(extensionId: string, version: string, platform?: 'win32' | 'darwin', idempotencyKey?: string): Promise<void>;
  withdrawExtensionRelease(extensionId: string, platform?: 'win32' | 'darwin', idempotencyKey?: string): Promise<void>;
  // admin read
  listUsers(): Promise<AdminUserView[]>;
  listUsers(query: UserListQuery): Promise<Paginated<AdminUserView>>;
  listGroups(): Promise<AdminGroupView[]>;
  listModelAccess(): Promise<TenantModelAccess[]>;
  listCatalog(): Promise<DesktopModelView[]>;
  listModelRoutes(): Promise<ModelRouteAdminView[]>;
  listQuotas(): Promise<QuotaPolicy[]>;
  usageSummary(query?: UsageQuery): Promise<UsageSummaryView>;
  usageSeries(query?: UsageSeriesQuery): Promise<UsageSeriesPoint[]>;
  usageEvents(query?: UsageQuery): Promise<Paginated<UsageEventView>>;
  usageExport(query?: UsageQuery): Promise<string>;
  creditSettlement(query?: CreditSettlementQuery): Promise<CreditSettlementReport>;
  creditSettlementExport(query?: CreditSettlementQuery): Promise<string>;
  /** Re-walk the tenant usage hash chain over a window and report integrity. */
  usageVerify(query?: UsageQuery): Promise<UsageChainVerification>;
  listAudit(): Promise<AdminAuditEvent[]>;
  listAudit(query: AuditListQuery): Promise<Paginated<AdminAuditEvent>>;
  getProviderKeys(tenantId: string): Promise<ProviderKeySummary[]>;
  listPlatformProviderModels(
    query?: PlatformProviderModelListQuery
  ): Promise<Paginated<PlatformProviderModelDefinition>>;
  createPlatformProviderModel(
    input: CreatePlatformProviderModelRequest,
    idempotencyKey?: string
  ): Promise<PlatformProviderModelDefinition>;
  updatePlatformProviderModel(
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: Omit<CreatePlatformProviderModelRequest, 'provider' | 'modelKey'> & {
      expectedRevision: number;
    },
    idempotencyKey?: string
  ): Promise<PlatformProviderModelDefinition>;
  testPlatformProviderModel(
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string
  ): Promise<PlatformProviderModelTestResult>;
  deployPlatformProviderModel(
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: { tenantIds: string[]; expectedRevision: number; rate: PlatformProviderModelRateInput },
    idempotencyKey?: string
  ): Promise<DeployPlatformProviderModelResult>;
  bulkDeployPlatformProviderModels(
    tenantId: string,
    input: BulkDeployPlatformProviderModelsRequest,
    idempotencyKey?: string
  ): Promise<BulkDeployPlatformProviderModelsResult>;
  publishPlatformProviderModelRates(
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: { tenantIds: string[]; expectedRevision: number; rate: PlatformProviderModelRateInput },
    idempotencyKey?: string
  ): Promise<PublishPlatformProviderModelRatesResult>;
  revokePlatformProviderModelDeployment(
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string,
    idempotencyKey?: string
  ): Promise<PlatformProviderModelDeployment>;
  disablePlatformProviderModel(
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    idempotencyKey?: string
  ): Promise<PlatformProviderModelDefinition>;
  listWebSearchBackends(tenantId: string): Promise<TenantWebSearchBackendSummary[]>;
  listMediaRoutes(tenantId: string): Promise<TenantMediaRouteSummary[]>;
  getPlatformWebSearchKey(): Promise<PlatformWebSearchKeySummary>;
  setPlatformWebSearchKey(input: { apiKey: string }, idempotencyKey?: string): Promise<PlatformWebSearchKeySummary>;
  getCurrentRates(): Promise<CreditRateTableView | undefined>;
  getRateSettings(): Promise<CreditRateSettingsView>;
  putRateSettings(input: PutCreditRateSettingsRequest, idempotencyKey?: string): Promise<PutCreditRateSettingsResult>;
  adoptPublishedRateSettings(idempotencyKey?: string): Promise<CreditRateSettingsView>;
  testProviderKey(input: TestProviderKeyInput): Promise<ProviderKeyTestResult>;
  testLiteLLMConnection(input: TestLiteLLMConnectionInput): Promise<ProviderKeyTestResult>;
  getTenantCatalog(tenantId: string): Promise<DesktopModelView[]>;
  getTenantAssignment(tenantId: string): Promise<TenantAssignment>;
  getTenantLoginDefaultModel(): Promise<TenantLoginDefaultModelView>;
  // admin write (idempotencyKey optional ??auto-generated when missing)
  inviteUser(email: string, name: string, roles?: ServiceRole[], idempotencyKey?: string): Promise<InvitedUser>;
  createUserWithPassword(
    email: string,
    name: string,
    password: string,
    roles?: ServiceRole[],
    idempotencyKey?: string
  ): Promise<{ user: ServiceUser }>;
  updateUserRoles(
    userId: string,
    roles: ServiceRole[],
    idempotencyKey?: string
  ): Promise<{ ok: true }>;
  disableUser(userId: string, idempotencyKey?: string): Promise<{ ok: true }>;
  enableUser(userId: string, idempotencyKey?: string): Promise<{ ok: true }>;
  deleteUser(userId: string, idempotencyKey?: string): Promise<{ ok: true }>;
  grantPlatformAdmin(
    input: { email: string; name: string; password?: string },
    idempotencyKey?: string
  ): Promise<{ userId: string }>;
  revokePlatformAdmin(
    userId: string,
    idempotencyKey?: string
  ): Promise<{ ok: true }>;
  assignSeat(
    userId: string,
    seatStatus: 'assigned' | 'unassigned' | 'suspended',
    idempotencyKey?: string
  ): Promise<{ ok: true }>;
  createGroup(name: string, idempotencyKey?: string): Promise<Group>;
  updateGroupMembers(
    groupId: string,
    patch: { addUserId?: string; removeUserId?: string },
    idempotencyKey?: string
  ): Promise<AdminGroupView>;
  updateAllowlist(
    userId: string,
    allowedModelIds: string[],
    defaultModelId: string,
    idempotencyKey?: string
  ): Promise<TenantModelAccess>;
  updateScopedAllowlist(
    subjectType: 'user' | 'group',
    subjectId: string,
    allowedModelIds: string[],
    defaultModelId: string,
    idempotencyKey?: string
  ): Promise<TenantModelAccess>;
  updateTenantLoginDefaultModel(
    loginDefaultModelId: string | null,
    idempotencyKey?: string
  ): Promise<TenantLoginDefaultModelView>;
  createModelRoute(
    route: ModelRoute,
    catalogItem: Parameters<
      ServiceClient['createModelRoute']
    >[1]['catalogItem'],
    idempotencyKey?: string
  ): Promise<ModelRouteAdminView>;
  removeCustomProviderModel(
    catalogModelId: string,
    idempotencyKey?: string
  ): Promise<void>;
  updateDownloadAccess(
    userId: string,
    patch: {
      desktopAccess?: boolean;
      downloadChannels?: DownloadChannel[];
      allowedPlatforms?: DesktopPlatform[];
      minimumDesktopVersion?: string;
    },
    idempotencyKey?: string
  ): Promise<Entitlement>;
  previewPolicy(draft: MergePolicyInput): Promise<PolicyContextSnapshot>;
  registerProviderKey(
    input: {
      tenantId: string;
      provider: PlatformProviderKeyProvider;
      apiKey: string;
      baseUrl?: string;
      litellmBaseUrl?: string;
    },
    idempotencyKey?: string
  ): Promise<ProviderKeyRegistrationResult>;
  removeProviderKey(
    tenantId: string,
    provider: PlatformProviderKeyProvider,
    idempotencyKey?: string
  ): Promise<ProviderKeyRemovalResult>;
  setTenantCustomerModelLabels(
    tenantId: string,
    labels: Record<string, { providerName?: string; modelName?: string }>,
    idempotencyKey?: string
  ): Promise<Tenant>;
  setWebSearchBackend(
    input: {
      tenantId: string;
      provider: WebSearchNativeProvider;
      status: TenantWebSearchBackend['status'];
      maxUsesDefault?: number;
    },
    idempotencyKey?: string
  ): Promise<TenantWebSearchBackendSummary>;
  setMediaRoute(
    input: {
      tenantId: string;
      kind: TenantMediaRoute['kind'];
      provider: TenantMediaRoute['provider'];
      status: TenantMediaRoute['status'];
      model: string;
      endpointRegion?: string;
      supportsZdr: boolean;
      supportsMam: boolean;
      maxUsesDefault?: number;
      requiredDataRegion?: string;
      requireZdr: boolean;
      requireMam: boolean;
      externalApis: 'allow' | 'deny';
    },
    idempotencyKey?: string
  ): Promise<TenantMediaRouteSummary>;
  setTenantAssignment(
    tenantId: string,
    allowedModelIds: string[],
    defaultMemberModelIds: string[],
    freeTierModelIds?: string[],
    idempotencyKey?: string
  ): Promise<TenantAssignment>;
  // downloads
  downloadLatestForUser(input: {
    userId: string;
    channel: DownloadChannel;
    platform: DesktopPlatform;
  }): Promise<DownloadManifest>;
}
export function defaultIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    return crypto.randomUUID();
  return `idem_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export interface AdminClientContext {
  assertAuthTransport(): void;
  transport: ServiceClient;
  token: () => string;
  idemKey: () => string;
  request: <T>(
    method: string,
    path: string,
    accessToken: string,
    body?: unknown,
    idempotencyKey?: string
  ) => Promise<T>;
}

export function createAdminClientContext(
  opts: AdminHttpOptions
): AdminClientContext {
  const transport: ServiceClient = createServiceClient({
    kind: 'http',
    options: opts,
  });
  const idemKey = () => opts.randomUUID?.() ?? defaultIdempotencyKey();

  async function request<T>(
    method: string,
    path: string,
    accessToken: string,
    body?: unknown,
    idempotencyKey?: string
  ): Promise<T> {
    const headers: Record<string, string> = {
      accept: 'application/json',
      authorization: `Bearer ${accessToken}`,
    };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;
    const fetcher = opts.fetchImpl ?? fetch;
    const response = await fetcher(`${opts.baseUrl.replace(/\/+$/, '')}${path}`, {
      method,
      headers,
      signal: AbortSignal.timeout(30_000),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (response.status === 204) return undefined as T;
    const payload = await response.json().catch(() => undefined);
    if (!response.ok) {
      throw new ServiceClientError(response.status, payload ?? { error: response.statusText });
    }
    return payload as T;
  }

  function token(): string {
    const t = opts.getAccessToken();
    if (!t) throw new Error('no_access_token');
    return t;
  }

  const assertAuthTransport = () => {
    const url = opts.baseUrl || (typeof location === 'undefined' ? '' : location.origin);
    try { validateGatewayBaseUrl(url, import.meta.env?.DEV === true && import.meta.env?.VITE_ALLOW_LOCAL_SERVICE_HTTP === 'true'); }
    catch { throw new ServiceClientError(400, { error: 'session_transport_invalid' }); }
  };
  return { transport, token, idemKey, request, assertAuthTransport };
}
